import React, { ReactNode, useState, useEffect, useRef, useCallback } from "react";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import type { GridApi, GridColumnVisibilityModel, GridInitialState, DataGridProProps } from "@mui/x-data-grid-pro";
import { useUserProfile } from "../hooks";

export const TABLE_WIDTH = 1400;

const ACTIONS_COLUMN_BASE = {
  field: "actions",
  type: "actions",
  sortable: false,
  // Every row's only means of interacting with it (view/edit/delete/review, etc.) lives here —
  // hiding it would strand the user with no way to act on that row.
  hideable: false,
} as const;

/** Actions column width for a getActions that renders icon buttons only (e.g. a chevron or delete icon). */
export const ACTIONS_COLUMN_ICON = {
  ...ACTIONS_COLUMN_BASE,
  width: 50,
} as const;

/** Actions column width for a getActions that can render a text button (e.g. "View"/"Review"), even if only in some variants. */
export const ACTIONS_COLUMN_TEXT = {
  ...ACTIONS_COLUMN_BASE,
  width: 100,
} as const;

/** sx for the icon inside a "view" GridActionsCellItem (e.g. ArrowForwardIosIcon) in an actions column. */
export const ACTIONS_VIEW_ICON_SX: SxProps<Theme> = { color: (theme) => theme.palette.action.active };

/** sx for a text Button used as an actions-column action. */
export const ACTIONS_TEXT_BUTTON_SX: SxProps<Theme> = { textTransform: "none" };

/**
 * Standard DataGridPro behavior flags shared by every table using DataTableContainer — server-side
 * sorting/filtering means MUI's multi-column sort and density switcher aren't supported, and none
 * of these tables use row selection. Spread onto `<DataGridPro {...STANDARD_GRID_PROPS}>`.
 */
export const STANDARD_GRID_PROPS: Pick<
  DataGridProProps,
  "disableMultipleColumnsSorting" | "disableColumnMenu" | "disableDensitySelector" | "disableSelectionOnClick"
> = {
  disableMultipleColumnsSorting: true,
  disableColumnMenu: true,
  disableDensitySelector: true,
  disableSelectionOnClick: true,
};

/**
 * Standard column header title styling shared by most tables. Spread into a table's own `sx`
 * prop — deliberately untyped as `SxProps<Theme>` (which also allows arrays/functions) so it
 * stays a plain object literal that TypeScript will actually let you object-spread.
 */
export const STANDARD_COLUMN_HEADER_TITLE_SX = {
  ".MuiDataGrid-columnHeaderTitle": {
    fontSize: "18px",
    fontWeight: 700,
    lineHeight: "24px",
    letterSpacing: 0.17,
  },
};

function readSavedState(storageKey: string): GridInitialState | undefined {
  try {
    const saved = localStorage.getItem(storageKey);
    return saved ? (JSON.parse(saved) as GridInitialState) : undefined;
  } catch {
    return undefined;
  }
}

/**
 * DataGridPro only honors a `restoreState()`/`initialState` columnVisibilityModel if
 * `initialState.columns.columnVisibilityModel` (or the `columnVisibilityModel` prop) was already
 * present on the grid's very first internal state computation at mount — it latches an internal
 * `isUsingColumnVisibilityModel` flag once and never revisits it. Our `storageKey` is often derived
 * from an async-loaded account ID, so on a hard refresh it's still `undefined` on that first render,
 * which would otherwise latch the flag `false` forever and silently no-op every later restoreState()
 * call's column-visibility restoration. Seeding a model here — the real saved one if already known
 * synchronously, else the table's all-visible default — guarantees the flag latches `true` from the
 * start, so the async restoreState() path (see the effect below) can still take effect later.
 */
function withVisibilityModelSeeded(
  state: GridInitialState | undefined,
  defaultVisibilityModel: GridColumnVisibilityModel | undefined,
): GridInitialState | undefined {
  if (!defaultVisibilityModel) {
    return state;
  }
  return {
    ...state,
    columns: {
      ...state?.columns,
      columnVisibilityModel: state?.columns?.columnVisibilityModel ?? defaultVisibilityModel,
    },
  };
}

/**
 * DataGridPro's restoreState only reorders columns explicitly listed in
 * `initialState.columns.orderedFields` — any column present in the grid's `columns` prop but
 * absent from that saved list is appended after every restored column, regardless of where it
 * belongs in the table's actual default order (see MUI's gridColumnsUtils.js: "The columns not
 * registered in orderedFields will be placed after the imported columns"). Since a saved layout
 * necessarily predates any column added after it was saved, every newly added column would
 * otherwise land at the far right of every table with that layout persisted, until a user
 * manually reorders it or resets their layout. This inserts each such new field into the saved
 * order at the position it holds in `defaultVisibilityModel` — anchored immediately after
 * whichever neighboring default-order field the user's saved order already contains — instead of
 * leaving it to DataGridPro's own default of appending at the end.
 */
function withColumnOrderReconciled(
  state: GridInitialState | undefined,
  defaultVisibilityModel: GridColumnVisibilityModel | undefined,
): GridInitialState | undefined {
  const savedOrderedFields = state?.columns?.orderedFields;
  // Array.isArray (rather than a truthiness check) also guards against a corrupted or
  // unexpected-shape localStorage value — orderedFields is read straight from JSON.parse with no
  // runtime validation, and `new Set` below would throw on anything non-iterable.
  if (!defaultVisibilityModel || !Array.isArray(savedOrderedFields)) {
    return state;
  }

  const defaultOrder = Object.keys(defaultVisibilityModel);
  const savedSet = new Set(savedOrderedFields);
  const hasNewField = defaultOrder.some((field) => !savedSet.has(field));
  if (!hasNewField) {
    return state;
  }

  const orderedFields = [...savedOrderedFields];
  let anchorIndex = -1;
  for (const field of defaultOrder) {
    if (savedSet.has(field)) {
      anchorIndex = orderedFields.indexOf(field);
    } else {
      orderedFields.splice(anchorIndex + 1, 0, field);
      anchorIndex += 1;
    }
  }

  return {
    ...state,
    columns: {
      ...state?.columns,
      orderedFields,
    },
  };
}

export interface DataTableContainerChildrenParams {
  apiRef: React.MutableRefObject<GridApi>;
  initialState: GridInitialState | undefined;
  /**
   * True below 700px — collapse the Filter/Export/Columns toolbar buttons to icon-only. Computed
   * once here (rather than separately in each table) so every consumer — the DataGridPro
   * `localeText` prop and the toolbar component's own props — agrees on the exact same boolean on
   * every render, instead of each independently querying the media breakpoint.
   */
  isIconOnly: boolean;
  /**
   * Clears this table's saved column visibility/order/width from localStorage and remounts the
   * grid fresh against `defaultVisibilityModel`. Doesn't affect sorting or filters — those are
   * driven by the page's own URL search params on every current consumer, not by this persisted
   * state, so clearing it doesn't touch them. Pass through to a toolbar's reset/undo action (see
   * ResetLayoutButton).
   */
  resetLayout: () => void;
}

export interface DataTableContainerProps {
  /** Optional toolbar content rendered above the grid in a 52px row Stack. */
  toolbar?: ReactNode;
  /** Render prop receiving apiRef and initialState for the DataGridPro. */
  children: (params: DataTableContainerChildrenParams) => ReactNode;
  /**
   * Base name for localStorage persistence, e.g. "citations-table", or `invoices-table-${variant}`
   * for tables whose columns vary by tab/variant. Omit to disable persistence.
   *
   * DataTableContainer calls useUserProfile() itself and scopes storage per-account by appending
   * the account ID — so the effective storage key isn't known until that profile has loaded.
   * Persistence simply stays inert (nothing read or written) until then; once it resolves, any
   * state already saved under that key is still restored into the mounted grid via
   * `apiRef.restoreState`.
   */
  tableName?: string;
  /**
   * The table's default column visibility — must be a stable, code-defined constant (same one
   * passed to ColumnsToolbarButton). Required for hidden columns to survive a page refresh: see
   * withVisibilityModelSeeded's docs for why DataGridPro needs this present at the very first
   * render, before the account-scoped storage key may even be known.
   *
   * Its key order also doubles as the table's canonical column order — see
   * withColumnOrderReconciled's docs — so declare its keys in the same order as the table's
   * actual column list.
   */
  defaultVisibilityModel?: GridColumnVisibilityModel;
  /**
   * Maximum width of the Paper. Mirrors MUI Container's maxWidth API:
   * - number | string: explicit value (e.g. 1200 or "80%")
   * - false (default): no constraint — fills the parent container
   * - TABLE_WIDTH (1400): opt-in to the standard table width cap
   */
  maxWidth?: number | string | false;
  /** Escape hatch for one-off sx overrides on the Paper. */
  sx?: SxProps<Theme>;
}

export function DataTableContainer({ tableName, ...rest }: DataTableContainerProps): JSX.Element {
  const { userProfile } = useUserProfile();
  const storageKey = tableName && userProfile?.account?.id ? `${tableName}-${userProfile.account.id}` : undefined;

  // Bumping this forces DataTableContainerCore to fully remount (via the key below), which
  // re-runs its one-time initialState seed against whatever's left in localStorage — the same
  // thing a user would see after clearing storage and reloading. Simpler and more reliable than
  // hand-constructing a "default" GridInitialState covering every possible restored field
  // (column order/width aren't available here to reset piecemeal via apiRef).
  const [resetGeneration, setResetGeneration] = useState(0);
  // Resetting remounts DataTableContainerCore (below), which unmounts the current instance —
  // whose own "save on unmount" effect would otherwise immediately re-write the very state we
  // just cleared. This ref is shared across the old and new Core instances (it lives here, not
  // inside Core, so it survives the remount) purely to suppress that one save.
  const skipSaveOnUnmountRef = useRef(false);
  const resetLayout = useCallback(() => {
    // Only set the skip flag when storageKey is truthy — that's the only case where the
    // save-on-unmount effect below actually registers a cleanup to consume (and reset) it.
    // Setting it unconditionally can leave it stuck true forever if reset is clicked before
    // the account (and thus storageKey) has resolved, silently dropping a later, unrelated save.
    if (storageKey) {
      skipSaveOnUnmountRef.current = true;
      localStorage.removeItem(storageKey);
    }
    setResetGeneration((generation) => generation + 1);
  }, [storageKey]);

  return (
    <DataTableContainerCore
      key={resetGeneration}
      storageKey={storageKey}
      resetLayout={resetLayout}
      skipSaveOnUnmountRef={skipSaveOnUnmountRef}
      {...rest}
    />
  );
}

interface DataTableContainerCoreProps extends Omit<DataTableContainerProps, "tableName"> {
  storageKey: string | undefined;
  resetLayout: () => void;
  skipSaveOnUnmountRef: React.MutableRefObject<boolean>;
}

function DataTableContainerCore({
  toolbar,
  children,
  storageKey,
  defaultVisibilityModel,
  maxWidth = false,
  sx,
  resetLayout,
  skipSaveOnUnmountRef,
}: DataTableContainerCoreProps): JSX.Element {
  const apiRef = useGridApiRef();
  const theme = useTheme();
  // Below this width, collapse the Filter/Export/Columns toolbar buttons to icon-only. Uses a
  // literal pixel number rather than a breakpoint key since this app's theme breakpoints (see
  // themePrimary.tsx) don't line up with MUI's defaults.
  const isIconOnly = useMediaQuery(theme.breakpoints.down(700));

  // Read saved state once at mount. useState initializer runs only once.
  const [initialState] = useState<GridInitialState | undefined>(() =>
    withVisibilityModelSeeded(
      withColumnOrderReconciled(storageKey ? readSavedState(storageKey) : undefined, defaultVisibilityModel),
      defaultVisibilityModel,
    ),
  );

  // storageKey often depends on data that loads asynchronously (e.g. an account ID from a
  // GraphQL query) — on a hard page refresh that data isn't available yet on the first render,
  // so the synchronous read above returns undefined even though a saved layout exists. Once
  // storageKey transitions to its real value, restore it directly into the already-mounted grid
  // via the public restoreState API. mountStorageKeyRef tracks the key already covered by the
  // synchronous read above, so this doesn't redundantly re-restore on the very first render.
  const mountStorageKeyRef = useRef(storageKey);
  useEffect(() => {
    if (storageKey === mountStorageKeyRef.current) {
      return;
    }
    mountStorageKeyRef.current = storageKey;
    if (!storageKey) {
      return;
    }
    const saved = readSavedState(storageKey);
    if (!saved) {
      return;
    }
    try {
      const reconciled = withColumnOrderReconciled(saved, defaultVisibilityModel);
      // withColumnOrderReconciled always returns a defined result for a defined input (`saved`,
      // narrowed above) — this check exists only so the call below doesn't need a non-null
      // assertion, which this codebase's lint config forbids.
      if (reconciled) {
        apiRef.current.restoreState(reconciled);
      }
    } catch {
      // apiRef.current may not be ready if this fires before the grid mounts.
    }
  }, [apiRef, storageKey, defaultVisibilityModel]);

  // Save on unmount. Capture apiRef.current at effect setup time so the cleanup
  // closes over a stable reference — MUI may clear .current during grid teardown.
  useEffect(() => {
    if (!storageKey) {
      return;
    }
    const api = apiRef.current;
    return () => {
      // resetLayout sets this right before remounting us specifically to reach this cleanup —
      // without the check, we'd immediately re-save the state resetLayout just cleared.
      if (skipSaveOnUnmountRef.current) {
        skipSaveOnUnmountRef.current = false;
        return;
      }
      try {
        const state = api.exportState();
        localStorage.setItem(storageKey, JSON.stringify(state));
      } catch {
        // api may not have exportState if the grid never fully mounted.
      }
    };
  }, [apiRef, storageKey, skipSaveOnUnmountRef]);

  // Save on page unload / tab backgrounding. beforeunload is unreliable on mobile (iOS Safari
  // and Android Chrome routinely skip it for bfcache/tab-discard), so pagehide covers the
  // desktop tab-close/navigate-away case instead — but pagehide itself still isn't guaranteed:
  // a mobile OS can kill a backgrounded tab's process outright (e.g. via the app switcher)
  // without ever dispatching it. visibilitychange->hidden fires the instant the tab
  // backgrounds, before any such kill, so it's kept as the reliable backstop alongside it.
  useEffect(() => {
    if (!storageKey) {
      return;
    }
    const saveState = () => {
      try {
        const state = apiRef.current.exportState();
        localStorage.setItem(storageKey, JSON.stringify(state));
      } catch {
        // apiRef.current may not have exportState if the grid never mounted.
      }
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        saveState();
      }
    };
    window.addEventListener("pagehide", saveState);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      window.removeEventListener("pagehide", saveState);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [apiRef, storageKey]);

  return (
    <Paper
      sx={{
        maxWidth: maxWidth === false ? undefined : { xs: "100%", md: maxWidth ?? TABLE_WIDTH },
        // Percentage height — requires every host page's own top-level wrapper to set an explicit
        // height/flexGrow down to this Paper (see the individual page components for that half of
        // the chain). Not a fixed pixel value: this table needs to actually fit within whatever
        // vertical space the viewport provides, on any screen size, without overflowing it.
        height: "100%",
        maxHeight: "100%",
        borderRadius: 2,
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        ...sx,
      }}
    >
      {toolbar && (
        <Stack
          direction="row"
          alignItems="center"
          px={1.25}
          sx={{ height: 52, flexShrink: 0, bgcolor: (theme: Theme) => theme.palette.background.paper }}
        >
          {toolbar}
        </Stack>
      )}
      {children({ apiRef, initialState, isIconOnly, resetLayout })}
    </Paper>
  );
}
